import { useEffect, useRef, useState } from 'react'
import type {
  AmmTelemetryView,
  CandleInterval,
  CandleSeries,
  LivePairSnapshot,
  Rate,
  TradeFeed,
  TradeView,
} from '@nutrimatic/sdk'
import { mergeCandleSeries } from '@nutrimatic/sdk'

import { nutrimaticErrorMessage } from '@/data/nutrimatic/errors'
import { resolvePairLabelForAddress } from '@/data/nutrimatic/fetchMarket'
import { mapCandlesToMarket, mapCandleInterval, mapTradesToRecent } from '@/data/nutrimatic/mapMarket'
import { getNutrimaticSdk } from '@/data/nutrimatic/sdk'
import type { MarketCandle, OhlcvInterval, RecentTrade } from '@/data/marketApi/types'
import { isNutrimaticConfigured } from '@/config/nutrimatic'

export type NutrimaticPairLiveState = {
  loading: boolean
  liveStatus: 'idle' | 'connecting' | 'live' | 'error'
  price: string
  change24h: string
  volume24h: string
  tvlUsd: number
  candles: MarketCandle[]
  recentTrades: RecentTrade[]
  error: string | null
  loadOlder: () => Promise<void>
  loadingOlder: boolean
}

const EMPTY: Omit<NutrimaticPairLiveState, 'loadOlder'> = {
  loading: false,
  liveStatus: 'idle',
  price: '—',
  change24h: '—',
  volume24h: '—',
  tvlUsd: 0,
  candles: [],
  recentTrades: [],
  error: null,
  loadingOlder: false,
}

function formatPrice(spot: Rate | undefined): string {
  if (!spot) return '—'
  const n = typeof spot.value === 'number' ? spot.value : Number(spot.value)
  if (!Number.isFinite(n)) return String(spot.value)
  if (n >= 1000) return n.toFixed(2)
  if (n >= 1) return n.toFixed(4)
  return n.toPrecision(4)
}

function formatChange(pool: AmmTelemetryView | undefined): string {
  const c = pool?.change24h?.value
  if (c == null || !Number.isFinite(c)) return '—'
  const pct = c * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

function formatUsd(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`
  return `$${n.toFixed(2)}`
}

function readSnap(snap: LivePairSnapshot) {
  return {
    spot: snap.spot as Rate | undefined,
    pool: snap.pool as AmmTelemetryView | undefined,
    trades: (snap.trades ?? []) as readonly TradeView[],
    chart: snap.chart,
  }
}

/**
 * Live market plane for a Don't Panic pair address (Lab `watchPair` pattern).
 */
export function useNutrimaticPairLive(
  pairAddress: string | undefined,
  params?: { interval?: OhlcvInterval | CandleInterval; chartLimit?: number; tradeLimit?: number },
): NutrimaticPairLiveState {
  const configured = isNutrimaticConfigured()
  const interval = mapCandleInterval(params?.interval ?? '1h')
  const chartLimit = params?.chartLimit ?? 200
  const tradeLimit = params?.tradeLimit ?? 50

  const [state, setState] = useState(EMPTY)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const liveGen = useRef(0)
  const chartRef = useRef<CandleSeries | null>(null)
  const pairLabelRef = useRef<string | null>(null)

  useEffect(() => {
    if (!configured || !pairAddress) {
      liveGen.current += 1
      setState(EMPTY)
      chartRef.current = null
      pairLabelRef.current = null
      return
    }

    const sdk = getNutrimaticSdk()
    if (!sdk) {
      setState({ ...EMPTY, liveStatus: 'error', error: 'SDK not configured' })
      return
    }

    const gen = ++liveGen.current
    setState((s) => ({
      ...s,
      loading: true,
      liveStatus: 'connecting',
      error: null,
    }))

    let unsub: (() => void) | undefined
    let cancelled = false

    void (async () => {
      try {
        const resolved = await resolvePairLabelForAddress(pairAddress)
        if (cancelled || gen !== liveGen.current) return
        if (!resolved) {
          setState({
            ...EMPTY,
            liveStatus: 'error',
            error: 'PAIR_NOT_FOUND',
          })
          return
        }
        pairLabelRef.current = resolved.pairLabel

        const sub = sdk.live.watchPair({
          pair: resolved.pairLabel,
          chart: { interval, limit: chartLimit },
          onUpdate: (snap) => {
            if (gen !== liveGen.current) return
            const { spot, pool, trades, chart } = readSnap(snap)
            if (chart) chartRef.current = chart
            setState({
              loading: false,
              liveStatus: 'live',
              price: formatPrice(spot),
              change24h: formatChange(pool),
              volume24h: formatUsd(pool?.volume24hUsd),
              tvlUsd: pool?.tvlUsd ?? 0,
              candles: mapCandlesToMarket(chart ?? chartRef.current),
              recentTrades: mapTradesToRecent(trades).slice(0, tradeLimit),
              error: null,
              loadingOlder: false,
            })
          },
          onError: (err) => {
            if (gen !== liveGen.current) return
            setState((s) => ({
              ...s,
              loading: false,
              liveStatus: 'error',
              error: err.message,
            }))
          },
        })
        unsub = () => sub.unsubscribe()

        try {
          const feed: TradeFeed = await sdk.easy.read.getTradeFeed({
            pair: resolved.pairLabel,
            limit: tradeLimit,
          })
          if (gen !== liveGen.current) return
          setState((s) => ({
            ...s,
            recentTrades: mapTradesToRecent(feed.events),
          }))
        } catch {
          /* keep live tip */
        }
      } catch (e) {
        if (gen !== liveGen.current) return
        setState({
          ...EMPTY,
          liveStatus: 'error',
          error: nutrimaticErrorMessage(e),
        })
      }
    })()

    return () => {
      cancelled = true
      unsub?.()
    }
  }, [configured, pairAddress, interval, chartLimit, tradeLimit])

  const loadOlder = async () => {
    const sdk = getNutrimaticSdk()
    const label = pairLabelRef.current
    const current = chartRef.current
    if (!sdk || !label || !current?.candles.length) return
    setLoadingOlder(true)
    try {
      const before = current.candles[0]?.time
      const older = await sdk.easy.read.getChart({
        pair: label,
        interval,
        limit: chartLimit,
        before,
      })
      const merged = mergeCandleSeries(current, older)
      chartRef.current = merged
      setState((s) => ({
        ...s,
        candles: mapCandlesToMarket(merged),
      }))
    } catch (e) {
      setState((s) => ({
        ...s,
        error: nutrimaticErrorMessage(e),
      }))
    } finally {
      setLoadingOlder(false)
    }
  }

  return {
    ...state,
    loadingOlder,
    loadOlder,
  }
}
